import './StagedTag.scss';
import { Dispatch, ReactElement, SetStateAction, useMemo } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdDelete, MdEdit } from 'react-icons/md';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner';
import { Appearance, Size } from '@Components/designConstants';
import { PieChart } from '@Components/PieChart';
import { useCreativeMap } from '../../../redux/creative';
import { StagedTagData, TagData } from './utils';

interface StagedTagProps {
  tag: StagedTagData;
  tagIndex: number;
  openTagCreationModal: (tag: TagData, isEditMode: boolean) => void;
  setStagedTags: Dispatch<SetStateAction<StagedTagData[]>>;
}

interface SliceData {
  name: string;
  value: number;
  color: string;
  creativeName: string;
  clickthroughUrl: string | null;
  audioCompanion: string | null;
}

const LegendItem = ({
  name,
  value,
  color,
  creativeName,
  clickthroughUrl,
  audioCompanion
}: SliceData): ReactElement => {
  const hasTooltip = Boolean(clickthroughUrl) || Boolean(audioCompanion);
  const swatch = (
    <div className='swatch-row'>
      <div className='swatch' style={{ backgroundColor: color }} />
      <div className='swatch-creative-name'>{creativeName}</div>
      <small className='swatch-isci-name'>({name})</small>
      <div className='swatch-value'> - {value}%</div>
    </div>
  );
  if (!hasTooltip) return swatch;
  return (
    <OverlayTrigger
      placement='top'
      overlay={
        <Tooltip>
          <div className='overlay-metadata'>
            {clickthroughUrl && (
              <div>
                <b>Clickthrough URL:</b> {clickthroughUrl}
              </div>
            )}
            {audioCompanion && (
              <div>
                <b>Audio Companion:</b> {audioCompanion}
              </div>
            )}
          </div>
        </Tooltip>
      }
    >
      {swatch}
    </OverlayTrigger>
  );
};

export const StagedTag = ({
  tag,
  tagIndex,
  openTagCreationModal,
  setStagedTags
}: StagedTagProps) => {
  const { company } = tag;
  const { creativeMap, colorMap } = useCreativeMap({
    company,
    mediaTypes: ['audio', 'display', 'streaming']
  });

  const rotationData = useMemo<SliceData[] | undefined>(() => {
    if (!tag.creativeAllocationMap || !creativeMap || !Object.keys(creativeMap).length) {
      return undefined;
    }
    return Object.keys(tag.creativeAllocationMap).map(isci => ({
      name: isci,
      value: parseInt(String(tag.creativeAllocationMap?.[isci] ?? 0), 10),
      color: colorMap[isci] || 'white',
      creativeName: creativeMap[isci]?.name || '',
      clickthroughUrl: tag.clickthroughUrlMap?.[isci] ?? null,
      audioCompanion: tag.audioCompanionMap?.[isci]?.adid ?? null
    }));
  }, [colorMap, creativeMap, tag]);

  const removeTag = () => setStagedTags(prev => prev.filter((_, i) => i !== tagIndex));

  return (
    <div className='staged-tag'>
      <div className='properties-div'>
        <div className='property-div'>
          <div className='property-title'>Name:</div>
          <div className='property-value'>{tag.name}</div>
        </div>
        <div className='property-div' hidden={!tag.vendor}>
          <div className='property-title'>Vendor:</div>
          <div className='property-value'>{tag.vendor}</div>
        </div>
        <div className='property-div' hidden={!tag.transcodeVendor}>
          <div className='property-title'>Transcode Vendor:</div>
          <div className='property-value'>{tag.transcodeVendor}</div>
        </div>
        <div className='property-div' hidden={!tag.adServer}>
          <div className='property-title'>Ad Server:</div>
          <div className='property-value'>{tag.adServer}</div>
        </div>
        <div className='property-div' hidden={!tag.clickthroughUrl}>
          <div className='property-title'>Placement-Level Clickthrough URL:</div>
          <div className='property-value'>{tag.clickthroughUrl}</div>
        </div>
        <div className='property-div' hidden={!tag.placementType}>
          <div className='property-title'>Flashtalking Placement Type:</div>
          <div className='property-value'>{tag.placementType}</div>
        </div>
        <div className='property-div' hidden={!tag.campaignID}>
          <div className='property-title'>{tag.adServer} Campaign ID:</div>
          <div className='property-value'>{tag.campaignID}</div>
        </div>
        <div className='property-div' hidden={!tag.dimensions}>
          <div className='property-title'>Dimensions:</div>
          <div className='property-value'>{tag.dimensions}</div>
        </div>
      </div>
      {rotationData ? (
        <div className='creatives-div'>
          <PieChart data={rotationData} size={100} />
          <div className='legend'>
            {rotationData.map(slice => (
              <LegendItem key={slice.name} {...slice} />
            ))}
          </div>
        </div>
      ) : (
        <Spinner />
      )}
      <div className='controls-div'>
        <IconButton
          appearance={Appearance.OUTLINED}
          size={Size.SM}
          onClick={() => {
            openTagCreationModal(tag, true);
            removeTag();
          }}
        >
          <MdEdit />
        </IconButton>
        <IconButton appearance={Appearance.OUTLINED} size={Size.SM} onClick={removeTag}>
          <MdDelete />
        </IconButton>
      </div>
    </div>
  );
};
